import { useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom, notificationsAtom } from '../store';
import { useAuth } from '../contexts/AuthContext';
import ImpersonationBanner from './ImpersonationBanner';
import AccountSwitcher from './AccountSwitcher';
import {
  LayoutGrid,
  Users,
  LogOut,
  Bell,
  RefreshCw,
  Settings,
  Database,
  Table,
  History,
  Webhook,
  FileInput,
  UserPlus,
  Calendar,
  List,
  Layers,
  FileText,
  MessageSquare,
  Building2,
} from 'lucide-react';

function navClass(active: boolean): string {
  return `flex items-center px-4 py-2 text-sm rounded-md ${
    active ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'
  }`;
}

/** Path match without hooks — safe inside conditionals. */
function pathIs(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

export default function Layout() {
  const ENABLE_LAYOUT_DEBUG = false;
  const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
  const [notifications] = useAtom(notificationsAtom);
  const { user, isAdmin, isSuperAdmin, impersonation, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const isDashboard = pathname === '/dashboard';
  // While impersonating, always show customer nav (session is target user)
  const showAdminNav = (isAdmin || isSuperAdmin) && !impersonation;

  // Sync AuthContext user to jotai atom
  useEffect(() => {
    if (user && (!currentUser || currentUser.id !== user.id)) {
      setCurrentUser({
        id: user.id,
        email: user.email || '',
        name: user.user_metadata?.name || user.email || '',
        role: user.user_metadata?.role || 'user',
        createdAt: user.created_at || '',
      });
    }
    if (!user && currentUser) {
      setCurrentUser(null);
    }
  }, [user, currentUser, setCurrentUser]);

  if (ENABLE_LAYOUT_DEBUG) {
    console.log('Layout Debug:', {
      isAdmin,
      isSuperAdmin,
      location: pathname,
      isDashboard,
      currentUser,
    });
  }

  const unreadNotifications = notifications.filter((n) => !((n as any).read)).length;
  const bannerOffset = impersonation ? 'pt-14' : '';

  const handleLogout = async () => {
    setCurrentUser(null);
    try {
      await signOut();
    } catch {
      navigate('/login');
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${bannerOffset}`}>
      {impersonation && <ImpersonationBanner state={impersonation} />}

      {/* Sidebar */}
      <div
        className={`fixed left-0 w-64 bg-white border-r border-gray-200 ${
          impersonation ? 'top-14 bottom-0' : 'inset-y-0'
        }`}
      >
        <div className="flex items-center h-16 px-6 border-b">
          <Link to="/" className="flex items-center space-x-2">
            <RefreshCw className="w-6 h-6 text-indigo-600" />
            <span className="text-lg font-semibold">4D Integration</span>
          </Link>
        </div>
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-4rem)]">
          <Link
            to={showAdminNav ? '/admin' : '/dashboard'}
            className={navClass(
              (isDashboard && !showAdminNav) || (pathname === '/admin' && showAdminNav)
            )}
          >
            <LayoutGrid className="w-5 h-5 mr-3" />
            Dashboard
          </Link>

          {showAdminNav ? (
            <>
              {(isSuperAdmin || isAdmin) && (
                <Link
                  to="/admin/accounts"
                  className={navClass(pathIs(pathname, '/admin/accounts'))}
                >
                  <Building2 className="w-5 h-5 mr-3" />
                  Accounts
                </Link>
              )}

              <Link
                to="/admin/integrations"
                className={navClass(pathIs(pathname, '/admin/integrations'))}
              >
                <Database className="w-5 h-5 mr-3" />
                Integrations
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/dashboard/integrations"
                className={navClass(pathIs(pathname, '/dashboard/integrations'))}
              >
                <Settings className="w-5 h-5 mr-3" />
                Integration Setup
              </Link>
              <Link
                to="/dashboard/data"
                className={navClass(pathIs(pathname, '/dashboard/data'))}
              >
                <Table className="w-5 h-5 mr-3" />
                Data Explorer
              </Link>
              <Link
                to="/dashboard/pipeline"
                className={navClass(pathIs(pathname, '/dashboard/pipeline'))}
              >
                <Layers className="w-5 h-5 mr-3" />
                Pipeline Mapping
              </Link>
              <Link
                to="/dashboard/patients"
                className={navClass(pathIs(pathname, '/dashboard/patients'))}
              >
                <Users className="w-5 h-5 mr-3" />
                Patients
              </Link>
              <Link
                to="/dashboard/appointments"
                className={navClass(pathIs(pathname, '/dashboard/appointments'))}
              >
                <Calendar className="w-5 h-5 mr-3" />
                Appointments
              </Link>
              <Link
                to="/dashboard/quotes"
                className={navClass(pathIs(pathname, '/dashboard/quotes'))}
              >
                <List className="w-5 h-5 mr-3" />
                Quotes
              </Link>
              <Link
                to="/dashboard/sync-logs"
                className={navClass(pathIs(pathname, '/dashboard/sync-logs'))}
              >
                <History className="w-5 h-5 mr-3" />
                Sync Logs
              </Link>
              <Link
                to="/dashboard/zonedm-transactions"
                className={navClass(pathIs(pathname, '/dashboard/zonedm-transactions'))}
              >
                <Webhook className="w-5 h-5 mr-3" />
                ZoneDM Transactions
              </Link>
              <Link
                to="/dashboard/fourdeemr-transactions"
                className={navClass(pathIs(pathname, '/dashboard/fourdeemr-transactions'))}
              >
                <FileInput className="w-5 h-5 mr-3" />
                4D Transactions
              </Link>
              <Link
                to="/dashboard/timeline-records"
                className={navClass(pathIs(pathname, '/dashboard/timeline-records'))}
              >
                <FileText className="w-5 h-5 mr-3" />
                Timeline Records
              </Link>
              <Link
                to="/dashboard/lead-notes"
                className={navClass(pathIs(pathname, '/dashboard/lead-notes'))}
              >
                <MessageSquare className="w-5 h-5 mr-3" />
                Lead Notes
              </Link>
              <Link
                to="/dashboard/webhook-logs"
                className={navClass(pathIs(pathname, '/dashboard/webhook-logs'))}
              >
                <Webhook className="w-5 h-5 mr-3" />
                Webhook Logs
              </Link>
              <Link
                to="/dashboard/leads-to-4d"
                className={navClass(pathIs(pathname, '/dashboard/leads-to-4d'))}
              >
                <UserPlus className="w-5 h-5 mr-3" />
                Leads to 4D
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Main content */}
      <div className="pl-64">
        <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
          <div className="flex items-center">
            <span className="text-sm text-gray-500">
              Welcome back, <span className="font-medium">{currentUser?.name}</span>
            </span>
          </div>
          <div className="flex items-center space-x-4">
            {/* Superadmin only (hidden while impersonating — banner has switcher) */}
            {(isSuperAdmin || isAdmin) && !impersonation && (
              <AccountSwitcher variant="toolbar" />
            )}
            <button
              type="button"
              className="relative p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-xs text-white flex items-center justify-center transform -translate-y-1 translate-x-1">
                  {unreadNotifications}
                </span>
              )}
            </button>
            <Link
              to="/dashboard/profile"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <Users className="w-5 h-5 mr-2" />
              Profile
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </button>
          </div>
        </div>

        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
